import React, {useEffect, useState} from 'react';
import {ActionFunction1, Action} from 'redux-actions';
import {useHistory} from 'react-router-dom';
import {useDebouncedCallback} from 'use-debounce';
import {baseRoutes} from 'App/AppRouter/routes';
import {setQuery} from '@pluto/set-history-query';
import useQuery from '@pluto/query-hook';
import {getInitialPage, getInitialRowsPerPage} from '@pluto/table';
import {DateRangeState, getInitialRanges} from '@pluto/daterange-picker';
import humanizeRange from '@pluto/humanize-moment-range';
import {
    handleMount,
    handlePaging,
    handleDebounce,
    handleDateRangeChange,
    handleDebounceCallback,
} from '@pluto/handlers';
import {setSearchParams} from 'QCWorkflow/QCManager/QCManagerContainer';
import {MediaOrderFilterParams} from '../MediaOrdersTypes';
import MediaOrdersList from './MediaOrdersList';
import {renderMediaOrderLink, getMediaOrdersFilters} from './MediaOrdersListHandlers';
import {MediaOrdersFilterHandlers, MediaOrdersListItem} from './MediaOrdersListTypes';

const DEBOUNCE_TIME = 700;
const route = baseRoutes.mediaOrders;

interface Props {
    total: number;
    mediaOrdersList: MediaOrdersListItem[];
    setMediaOrders: ActionFunction1<MediaOrderFilterParams, Action<Promise<undefined>>>;
    resetMediaOrders: () => void;
}

export default function MediaOrdersListContainer(props: Props) {
    const {total, mediaOrdersList, setMediaOrders: setList, resetMediaOrders} = props;
    const query = useQuery();
    const history = useHistory();
    const [{contentPartnerId, labPartner}, setFilter] = useState({
        contentPartnerId: query.get('contentPartnerId') || '',
        labPartner: query.get('labPartner') || '',
    });
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);
    const [mediaOrderId, setMediaOrderId] = useState(query.get('mediaOrderId') || '');
    const initialDateIssued = getInitialRanges(query, 'dateIssuedStart', 'dateIssuedEnd');
    const initialImportDate = getInitialRanges(query, 'importDateStart', 'importDateEnd');
    const [dateIssued, setDateIssued] = useState<DateRangeState>(initialDateIssued);
    const [importDate, setImportDate] = useState<DateRangeState>(initialImportDate);
    const offset = rowsPerPage * page;

    const [importDateStart, importDateEnd] = humanizeRange(importDate);
    const [dateIssuedStart, dateIssuedEnd] = humanizeRange(dateIssued);

    const params = {
        contentPartnerId,
        labPartner,
        mediaOrderId,
        offset,
        limit: rowsPerPage,
        importDateStart,
        importDateEnd,
        dateIssuedStart,
        dateIssuedEnd,
    };

    const tableColumns = [
        {
            dataKey: 'mediaOrderId',
            title: 'ID',
            render: renderMediaOrderLink(route),
        },
        {
            dataKey: 'partnerName',
            title: 'Content partner',
        },
        {
            dataKey: 'labPartner',
            title: 'Lab partner',
        },
        {
            dataKey: 'dateIssued',
            title: 'Date Issued',
        },
        {
            dataKey: 'createdAt',
            title: 'Import Date',
        },
    ];

    const filterHandlers = {
        setFilter,
        setPage,
        setQuery,
        history,
        setList,
        contentPartnerId,
        labPartner,
        limit: rowsPerPage,
        mediaOrderId,
        importDateStart,
        importDateEnd,
        dateIssuedStart,
        dateIssuedEnd,
        setImportDate,
        setDateIssued,
        route,
    };

    const {handleContentPartnersChange, handleLabPartnersChange} = getMediaOrdersFilters(
        filterHandlers
    );

    const debounceCallback = handleDebounceCallback({
        contentPartnerId,
        labPartner,
        mediaOrderId,
        setList,
        setPage,
        limit: rowsPerPage,
        offset: 0,
        setQuery,
        history,
        route,
        importDateStart,
        importDateEnd,
        dateIssuedStart,
        dateIssuedEnd,
    });

    const [debouncedFunction] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);

    const onDebouncedSearch = handleDebounce({
        debouncedFunction,
        setSearchFilter: setMediaOrderId,
    });

    const onPaging = handlePaging({
        ...params,
        setList,
        setQuery,
        history,
        route,
    });

    const onMount = handleMount({
        setList,
        resetList: resetMediaOrders,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);
    useEffect(setSearchParams(query), [query]);

    const filters = {
        contentPartnerId,
        labPartner,
    };

    const onDateIssuedSelect = handleDateRangeChange(
        filterHandlers as MediaOrdersFilterHandlers,
        'dateIssued',
        setDateIssued
    );
    const onImportDateSelect = handleDateRangeChange(
        filterHandlers as MediaOrdersFilterHandlers,
        'importDate',
        setImportDate
    );

    return (
        <MediaOrdersList
            total={total}
            data={mediaOrdersList}
            columns={tableColumns}
            idValue={mediaOrderId}
            handleIdChange={onDebouncedSearch}
            handleContentPartnersChange={handleContentPartnersChange}
            handleLabPartnersChange={handleLabPartnersChange}
            filters={filters}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            setFilter={setFilter}
            onPaging={onPaging}
            setMediaOrderId={setMediaOrderId}
            dateIssued={dateIssued}
            importDate={importDate}
            onDateIssuedSelect={onDateIssuedSelect}
            onImportDateSelect={onImportDateSelect}
        />
    );
}
